/**
 * urlState.ts
 *
 * Pure helpers for the Workspace URL state. The URL holds the active Mode and
 * the current selection (`mode`, `loan`, `doc`, `field`, `exception`) so views
 * are linkable and survive a reload.
 *
 * No React and no router dependency: everything operates on `URLSearchParams`
 * (or a query string) and plain objects.
 *
 * Requirements: 3.3 (Mode survives reload and can be linked).
 */

import { JobType } from '../graphql/types';

/** Every Mode that any Workspace can show. */
export type WorkspaceMode =
  | 'intake'
  | 'extract'
  | 'analyze'
  | 'review'
  | 'findings'
  | 'analysis';

/** Valid Modes per Feature, in display order. DD and BA share one set. */
export const WORKSPACE_MODES: Readonly<Record<JobType, readonly WorkspaceMode[]>> = {
  [JobType.DD]: ['intake', 'extract', 'analyze'],
  [JobType.BA]: ['intake', 'extract', 'analyze'],
  [JobType.TR]: ['intake', 'review', 'findings', 'analysis'],
};

/** Selection params carried in the URL alongside the Mode. */
export const SELECTION_PARAM_KEYS = ['loan', 'doc', 'field', 'exception'] as const;
export type SelectionParamKey = (typeof SELECTION_PARAM_KEYS)[number];

export interface WorkspaceParams {
  mode: WorkspaceMode;
  loan?: string;
  doc?: string;
  field?: string;
  exception?: string;
}

/** Input accepted by `writeWorkspaceParams`; `null`/`undefined`/`''` clears a key. */
export type WorkspaceParamsUpdate = {
  mode?: WorkspaceMode | null;
} & {
  [K in SelectionParamKey]?: string | null;
};

type SearchInput = URLSearchParams | string | null | undefined;

function toSearchParams(search: SearchInput): URLSearchParams {
  if (search instanceof URLSearchParams) {
    return new URLSearchParams(search);
  }
  return new URLSearchParams(search ?? '');
}

/** True when `value` is a valid Mode for the given Feature. */
export function isValidMode(feature: JobType, value: unknown): value is WorkspaceMode {
  return (
    typeof value === 'string' &&
    (WORKSPACE_MODES[feature] as readonly string[]).includes(value)
  );
}

/**
 * Read Workspace state from the URL.
 *
 * A missing or invalid `mode` falls back to `defaultMode` (the Row → Mode
 * default). If `defaultMode` is omitted or not valid for the Feature, the
 * Feature's first Mode (`intake`) is used. Selection params that are absent or
 * empty are omitted from the result.
 */
export function readWorkspaceParams(
  search: SearchInput,
  feature: JobType,
  defaultMode?: WorkspaceMode
): WorkspaceParams {
  const params = toSearchParams(search);
  const rawMode = params.get('mode');
  const fallback = isValidMode(feature, defaultMode)
    ? defaultMode
    : WORKSPACE_MODES[feature][0];
  const result: WorkspaceParams = {
    mode: isValidMode(feature, rawMode) ? rawMode : fallback,
  };
  for (const key of SELECTION_PARAM_KEYS) {
    const value = params.get(key);
    if (value) {
      result[key] = value;
    }
  }
  return result;
}

/**
 * Write Workspace state onto a copy of `search`, returning new `URLSearchParams`.
 *
 * Only keys present in `update` change: a string value sets the key, while
 * `null`, `undefined` or `''` removes it. Keys not in `update` and unrelated
 * params are preserved. The input is never mutated.
 */
export function writeWorkspaceParams(
  search: SearchInput,
  update: WorkspaceParamsUpdate
): URLSearchParams {
  const params = toSearchParams(search);
  const keys = ['mode', ...SELECTION_PARAM_KEYS] as const;
  for (const key of keys) {
    if (!(key in update)) continue;
    const value = update[key];
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
  }
  return params;
}
